import { INFOCENTER_ROUTES } from "./routes"

/**
 * Short help texts for the Digital Shelf overview columns, shown as a header tooltip.
 *
 * Keyed by the `key` used in `Pages/DigitalShelf/General/Table/index.jsx`, so a new column
 * gets its tooltip by adding an entry here — the table needs no change.
 *
 * `section` is the id of the matching card in the Info Center glossary
 * (`Pages/InfoCenter/KpiGlossary/content.js`), which is what the `?` links to. Keeping it
 * next to the text is what stops the two from drifting apart: one entry, one KPI.
 *
 * ⚠️ The target is a **query parameter and not a `#` anchor**, which it would naturally be.
 * Opening the link in a new tab is a full page load, so it goes through the session check in
 * `Layout`, and that builds its redirect out of `pathname + search` — dropping the hash on the
 * way. The parameter survives the round trip; an anchor arrives empty.
 */
const help = (section, ...lines) => ({
	text: lines.join('\n'),
	section,
	path: `${INFOCENTER_ROUTES.KPI_GLOSSARY_PATH}?kpi=${section}`
})

/**
 * The texts are the short descriptions Mattel validated, one argument per tooltip line.
 *
 * Share of Search only counts the first 100 positions for Mattel (`EXTENDED_GLOBAL_POSITION_LIMIT`
 * in the backend, 20 for every other customer). The texts state 100 because Mattel is the only
 * customer with this dashboard.
 */
export const DIGITAL_SHELF_KPI_HELP = {
	average: help('digital-shelf-score',
		'The average of the four scores to its right — Stock, Assortment, Product Page and Share of Search.',
		'Scores that cannot be measured are left out of the average rather than counted as zero.'),

	stock_kpi: help('stock-score',
		'Percentage of products in the retailer listing that were available to purchase (in stock) during the ' +
		'selected period.',
		'The score considers every product in the retailer listing across every day in the selected time period.'),

	assortment_kpi: help('assortment-score',
		'Percentage of products from the retailer listing that were found on the retailer website.',
		'Expected products are determined by the retailer listing.',
		'e.g. 100 products in the retailer listing, 50 products found on the retailer = 50% Assortment Score'),

	pp_kpi: help('product-page-score',
		'Weighted quality score for products in the retailer listing based on their product page content.',
		'Based on the metrics found in the Product Page dashboard (title, descriptions, bullet points, images, ' +
		'video, Rich content (A+)). Product Page metrics that cannot be measured are excluded from the score.'),

	sos_kpi: help('share-of-search-score',
		'Percentage of tracked search results that contain products from the retailer listing.',
		'Both organic and sponsored are included.',
		'The denominator is every result within the first 100 positions, both yours and the competition\'s.',
		'e.g. 100 results, 50 are within the retailer listing = 50% Share of Search'),

	sos_organic_kpi: help('share-of-search-organic-score',
		'The part of the Share of Search score that comes from organic results. Organic = unpaid results.',
		'The denominator is every result within the first 100 positions, including sponsored and organic, both ' +
		'yours and the competition\'s.'),

	sos_sponsored_kpi: help('share-of-search-sponsored-score',
		'The part of the Share of Search score that comes from sponsored results. Sponsored = paid results.',
		'The denominator is every result within the first 100 positions, including sponsored and organic, both ' +
		'yours and the competition\'s.')
}
